'use client';

import React, { useContext, useEffect, useRef } from 'react';
import { BloodVisionPageProps } from './types';
import ZipCodeInput from './ZipCodeInput';
import FeatureSection from './FeatureSection';
import { XLDivider } from './FeatureList';
import AddOnList from './AddOnList';
import OrderSummary from './OrderSummary';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { useBloodVision } from '../BloodVisionContext';
import ModalitySection from './ModalitySection';
import { OffersCarousel } from 'components/ring-air/OffersCarousel';
import HSASection from './HSASection';
import Image from 'next/image';
import BirthdayIcon from '../../../assets/birthday-icon.svg';
import { ImageModal } from 'components/ring-air/ProductSelectionSection/ImageModal';
import { HsaFsaOffer } from './HsaFsaOffer';
import { useBvFreeCampaignModal } from './BvFreeCampaignModal';
import { ZipCodeContext } from './ZipCodeContext';
import styles from './RightPanelComponent.module.css';

export interface FeatureProps {
  title: string;
  description: string;
}

const RightPanelComponent: React.FC<BloodVisionPageProps> = () => {
  const { width } = useWindowSize();
  const isMobile = width < 992;
  const BloodVisionClient = useBloodVision();
  const {
    zipCode,
    setZipCode,
    isLoading,
    selectedPlan,
    addOns,
    selectedAddOns,
    toggleAddOn,
    total,
    handleCheckout,
    showDiscount,
    discount,
    affiliateDetails,
    effectiveDiscount,
    isZipValid,
    defaultDiscount,
    uiComponents,
  } = BloodVisionClient;

  const {
    zipCodeInputRef,
    setModalitySectionRef,
    setHsaSectionRef,
    modalitySectionRef,
    hsaSectionRef,
  } = useContext(ZipCodeContext);

  const localModalitySectionRef = useRef<HTMLDivElement>(null);
  const localHsaSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (setModalitySectionRef) {
      setModalitySectionRef(localModalitySectionRef);
    }
    if (setHsaSectionRef) {
      setHsaSectionRef(localHsaSectionRef);
    }
  }, [setModalitySectionRef, setHsaSectionRef]);

  // Check if plan has is_plan_campaign: true
  const isCampaignActive = React.useMemo(() => {
    if (!selectedPlan) return false;
    const isCampaign = selectedPlan.is_plan_campaign === true;
    return isCampaign;
  }, [selectedPlan]);

  // Use the campaign modal hook
  const { showCampaignModal } = useBvFreeCampaignModal();

  // Wrapper function for checkout that shows modal for campaign plans
  const handleCheckoutClick = () => {
    if (isCampaignActive) {
      // Show the campaign modal instead of proceeding to checkout
      showCampaignModal();
    } else {
      // Normal checkout flow
      handleCheckout();
    }
  };

  const secondaryOfferData = [
    {
      title: (
        <>
          <p>
            <b>HSA/FSA</b> | Save more with your pre-tax dollars
          </p>
        </>
      ),
      modal: (
        <ImageModal>
          <HsaFsaOffer />
        </ImageModal>
      ),
    },
  ];

  return (
    <div className={styles.rightPanel} id="blood-vision-buy-plans">
      {!isMobile ? (
        <div className={styles.header}>
          <div className={`${styles.titleContainer} title-container`}>
            <h2 className={styles.h2}>Blood Vision</h2>
          </div>
          <div style={{ width: '100%', padding: '10px 0' }}>
            <span
              style={{
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: '400',
                lineHeight: '120%',
                letterSpacing: '-0.56px',
                color: '#000000',
                position: 'relative',
                bottom: '4px',
              }}
            >
              {/* {selectedPlan?.description ||
                'Includes one comprehensive test and one follow-up, both with full blood and urine analysis.'} */}
              Comprehensive biomarker testing for metabolic, cardiovascular,
              immune, hormonal, and nutritional health, with AI-powered clinical
              summary and personalized supplement and lifestyle recommendations.
            </span>
          </div>

          {/* Discount display */}
          {uiComponents.offer_section && (
            <>
              <div
                className="offerFlex"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  padding: '8px 0',
                  // marginTop: '8px',
                }}
              >
                {/* Birthday discount tag */}
                {showDiscount && discount && (
                  <p className={styles.offerTag}>
                    <Image
                      src={BirthdayIcon}
                      alt="birthday_icon"
                      style={{
                        borderRadius: '0',
                      }}
                    />
                    <span>Birthday Discount Applied</span>
                  </p>
                )}

                {/* Affiliate discount tag */}
                {effectiveDiscount.type === 'affiliate' &&
                  affiliateDetails &&
                  selectedPlan && (
                    <p className={`${styles.offerTag} ${styles.transparent}`}>
                      <span className=" ">
                        {Math.round(
                          (effectiveDiscount.amount /
                            (selectedPlan.price_split?.base_price ||
                              selectedPlan.original_price)) *
                            100,
                        )}
                        % off applied via {affiliateDetails.code}
                      </span>
                    </p>
                  )}

                {/* Black Friday discount tag */}
                {effectiveDiscount.type === 'black_friday' && defaultDiscount && (
                  <p className={styles.offerTag}>
                    <span>
                      <span className={styles.offerApplied}>NY26</span> Offer
                      Applied
                    </span>
                  </p>
                )}
              </div>

              {secondaryOfferData && (
                <OffersCarousel data={secondaryOfferData} leftAligned={true} />
              )}
            </>
          )}
        </div>
      ) : null}
      {uiComponents.zip_section && (
        <ZipCodeInput
          zipCode={zipCode}
          setZipCode={setZipCode}
          isLoading={isLoading}
          ref={zipCodeInputRef || undefined}
        />
      )}
      <div className={styles.zipToFeatureSectionSpacer} />
      <FeatureSection />
      {uiComponents.testing_location_section && (
        <>
          <XLDivider />
          <ModalitySection
            ref={modalitySectionRef || localModalitySectionRef}
            isCampaignActive={isCampaignActive}
          />
        </>
      )}
      {!isCampaignActive && uiComponents.hsa_fsa_section && (
        <>
          <XLDivider />
          <HSASection ref={hsaSectionRef || localHsaSectionRef} />
        </>
      )}
      {/* <XLDivider /> */}
      {isZipValid && selectedPlan && (
        <>
          <XLDivider />
          {addOns && addOns.length > 0 ? (
            <>
              <div className={styles.personalizeSection}>
                <div className={styles.personalizeTitle}>
                  <h3>Personalize your Plan</h3>
                </div>

                <AddOnList
                  addOns={addOns}
                  selectedAddOns={selectedAddOns}
                  toggleAddOn={toggleAddOn}
                />
              </div>
              <XLDivider />
            </>
          ) : null}

          <OrderSummary
            mainPlan={selectedPlan}
            selectedAddOns={addOns.filter((addon) =>
              selectedAddOns.includes(addon.id),
            )}
            total={total}
            onCheckout={handleCheckoutClick}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
};

export default RightPanelComponent;
